'use strict';

// Browser entry for the "Calm Premium" consumer app (public/app.html).
// Wires the real non-custodial engine — register / login / unlock / lock — to the
// 4-tab UI and renders the user's REAL self-owned identity (DID, name, keys).
// The presentation helpers (tab nav, toast, collection rendering, player) live
// inline in app.html; this module owns auth/session/identity only.

const identity = require('./crypto/identity');
const api = require('./api');
const { registerFlow, loginFlow, recoverFlow, serviceRecoveryRequest, serviceRecoveryVerify } = require('./engineflows');
const session = require('./session');
const devicevault = require('./devicevault');
const webauthn = require('./webauthn');
const vcjwt = require('./crypto/vcjwt');
const did = require('./crypto/did');
const docsign = require('./crypto/docsign');
const disclosure = require('./crypto/disclosure');
const bap = require('./crypto/bap');
const ordinals = require('./collections/ordinals');
const finance = require('./wallet/finance');
const qr = require('./wallet/qr');

const Web3Keys = Object.assign({}, identity, { api, registerFlow, loginFlow, vcjwt, did });
if (typeof window !== 'undefined') window.Web3Keys = Web3Keys;

// ---------- tiny DOM helpers ----------
const $ = (id) => document.getElementById(id);
const setText = (id, t) => { const el = $(id); if (el) el.textContent = t; };
const setShow = (el, on) => { if (el) el.style.display = on ? '' : 'none'; };
const toast = (m) => { if (typeof window !== 'undefined' && window.toast) window.toast(m); };

function friendly(e) {
  const m = (e && e.message) || String(e);
  if (/already exists/i.test(m)) return 'An account with that email already exists — try signing in.';
  if (/invalid email or password/i.test(m) || /decrypt|cipher|operation-specific/i.test(m)) return 'Incorrect email or password.';
  if (/invalid email/i.test(m)) return 'Please enter a valid email address.';
  return m;
}

// ---------- auth mode (register vs sign-in) ----------
let authMode = 'register';
let pendingSession = null; // session set after the post-backup "save recovery code" step
function setAuthMode(mode) {
  authMode = mode;
  const reg = mode === 'register';
  setText('gate-title', reg ? 'Create your account' : 'Welcome back');
  setText('gate-sub', reg ? 'Your keys, your identity — only you ever control them.' : 'Sign in to your account.');
  setText('gate-submit', reg ? 'Create my account' : 'Sign in');
  setText('gate-toggle', reg ? 'Already have an account? Sign in' : 'New here? Create an account');
  setErr('gate-err', '');
}
function setErr(id, msg) { const el = $(id); if (el) el.textContent = msg || ''; }

// ---------- identity rendering ----------
function displayName(email) {
  const n = (String(email || '').split('@')[0] || 'you');
  return n.charAt(0).toUpperCase() + n.slice(1);
}
function renderIdentity(st) {
  const email = st.email || '';
  const name = displayName(email);
  const local = (String(email).split('@')[0] || 'you').toLowerCase();
  window.__w3kName = name;
  if (window.refreshGreeting) window.refreshGreeting();
  setText('profileName', name);
  setText('handleLine', '@' + local + ' · web3keys.com');
  const av = $('avatarInitial'); if (av) av.textContent = name.charAt(0).toUpperCase();
  setText('didValue', st.did || '—');
  setText('didReal', '● your live, self-owned identity');
}

// ---------- first-run walkthrough (the identity + redeemable-wallet story, plain language) ----------
const ONBOARDING = [
  { icon: '🔐', title: 'Welcome to Web3Keys', body: 'One secure identity for everything you do online — plus a home for the value and assets you earn along the way. Only you hold the keys.' },
  { icon: '✨', title: 'One sign-in, everywhere', body: 'Sign in to SmartLedger apps with a tap. No new passwords to remember — you prove it’s you, and nobody else can.' },
  { icon: '💵', title: 'Money you can redeem', body: 'When an app owes you — royalties, credits, a payout — it shows up here as real value you can cash out whenever you want.' },
  { icon: '🎟️', title: 'All your stuff, in one place', body: 'Tickets, music, collectibles, signed documents — organized by the app that made them. Keep them, share them, or redeem them.' },
];
let obStep = 0;
function renderOnboarding() {
  const s = ONBOARDING[obStep];
  setText('ob-icon', s.icon); setText('ob-title', s.title); setText('ob-body', s.body);
  const dots = $('ob-dots'); if (dots) dots.innerHTML = ONBOARDING.map((_, i) => '<i class="' + (i === obStep ? 'on' : '') + '"></i>').join('');
  const back = $('ob-back'); if (back) back.style.visibility = obStep === 0 ? 'hidden' : 'visible';
  const next = $('ob-next'); if (next) next.textContent = obStep === ONBOARDING.length - 1 ? 'Get started' : 'Next';
}
function showOnboarding() { obStep = 0; renderOnboarding(); setShow($('onboarding'), true); }
function finishOnboarding() { try { localStorage.setItem('w3k-onboarded', '1'); } catch (_) {} setShow($('onboarding'), false); }
function maybeOnboard() {
  if (typeof navigator !== 'undefined' && navigator.webdriver) return; // don't pop the overlay during automated tests
  let seen; try { seen = localStorage.getItem('w3k-onboarded'); } catch (_) { seen = '1'; }
  if (!seen) showOnboarding();
}

// ---------- gate visibility ----------
function showGate(view) {
  setShow($('gate'), true);
  ['auth', 'backup', 'unlock', 'recover', 'recover-code', 'recover-pending', 'recover-newpass', 'savecode'].forEach((v) => setShow($('gate-' + v), v === view));
}
function openApp() {
  setShow($('gate'), false);
  renderIdentity(session.get());
  healDidDoc();
  refreshBioRow();
  refreshInbox();
  refreshCollection();
  refreshWallet();
  refreshDocuments();
  refreshPermissions();
  renderShareRows();
  refreshShareCards();
  refreshBap();
  refreshUsername();
  if (window.go) window.go('id');
  maybeOnboard(); // first-run walkthrough (skipped once seen / under automation)
}

// Self-heal: if the server still publishes a stale DID key (e.g. the pre-migration P-256 key),
// republish the current secp256k1 key — authorized by a signature from the identity key.
async function healDidDoc() {
  const st = session.get();
  if (!st.keys || !st.handle || !st.did || !window.bsv) return;
  try {
    const publicJwk = vcjwt.jwkFromPublicKey(st.keys.identity.privateKey.toPublicKey());
    let published = null;
    try { published = await fetch('/u/' + st.handle + '/jwks.json').then((r) => (r.ok ? r.json() : null)); } catch (_) {}
    const cur = published && published.keys && published.keys[0];
    if (cur && cur.x === publicJwk.x && cur.crv === publicJwk.crv) return; // already current
    const signature = window.bsv.Message.sign('republish:' + st.handle, st.keys.identity.privateKey);
    await api.republishDid(st.handle, { did: st.did, didDocument: did.buildDidDocument(st.did, publicJwk, 'key-1'), jwks: did.buildJwks(publicJwk, 'key-1'), signature });
  } catch (_) { /* ignore */ }
}

// ---------- backup gate (hard block: ack + 3 random words) ----------
function pickIndices(count, max) {
  const set = new Set();
  while (set.size < count) set.add(1 + Math.floor(Math.random() * max));
  return [...set].sort((a, b) => a - b);
}
function startBackup(phrase, onConfirmed) {
  const words = phrase.split(' ');
  const grid = $('mnemGrid');
  if (grid) grid.innerHTML = words.map((w, i) => `<div class="mword"><span>${i + 1}</span>${w}</div>`).join('');
  const indices = pickIndices(3, words.length);
  const fields = $('confirm-fields');
  const ack = $('backup-ack');
  const cont = $('backup-continue');
  setErr('backup-err', '');
  if (ack) ack.checked = false;
  const inputs = [];
  if (fields) {
    fields.innerHTML = '';
    for (const i of indices) {
      const wrap = document.createElement('div');
      wrap.className = 'cfield';
      wrap.innerHTML = `<label>Word #${i}</label>`;
      const inp = document.createElement('input');
      inp.type = 'text'; inp.autocomplete = 'off'; inp.spellcheck = false;
      inp.dataset.index = String(i);
      inp.addEventListener('input', () => setErr('backup-err', ''));
      wrap.appendChild(inp); fields.appendChild(wrap); inputs.push(inp);
    }
  }
  if (cont) cont.onclick = () => {
    if (ack && !ack.checked) { setErr('backup-err', 'Please confirm you have written down your phrase.'); return; }
    const ok = inputs.every((inp) => inp.value.trim().toLowerCase() === words[Number(inp.dataset.index) - 1].toLowerCase());
    if (!ok) { setErr('backup-err', 'Those words don’t match. Check your written copy and try again.'); return; }
    onConfirmed();
  };
  showGate('backup');
}

// ---------- flows ----------
async function handleAuth(ev) {
  if (ev) ev.preventDefault();
  const email = (($('gate-email') || {}).value || '').trim();
  const password = ($('gate-password') || {}).value || '';
  if (!email || !password) { setErr('gate-err', 'Enter your email and password.'); return; }
  const btn = $('gate-submit');
  const label = btn ? btn.textContent : '';
  if (btn) { btn.disabled = true; btn.textContent = 'Working…'; }
  setErr('gate-err', '');
  try {
    if (authMode === 'register') {
      const created = await registerFlow(email, password, { controllerName: email.split('@')[0] });
      await devicevault.remember(email, created.registration).catch(() => {});
      startBackup(created.phrase, () => {
        pendingSession = { email, did: created.did, handle: created.handle, jwks: created.jwks, keys: created.keys, mnemonic: null };
        if ($('savecode-value')) $('savecode-value').value = created.recoveryShareUser || '';
        showGate('savecode');
      });
    } else {
      const opened = await loginFlow(email, password);
      await devicevault.remember(email, opened).catch(() => {});
      session.set({ email, did: opened.did, handle: opened.handle, jwks: opened.jwks, keys: opened.keys, mnemonic: null });
      openApp();
    }
  } catch (e) {
    setErr('gate-err', friendly(e));
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = label; }
  }
}

async function handleUnlock(ev) {
  if (ev) ev.preventDefault();
  const st = session.get();
  const email = st.email || (($('gate-unlock') || {}).dataset || {}).email;
  const password = ($('unlock-password') || {}).value || '';
  if (!email) { setAuthMode('register'); showGate('auth'); return; }
  if (!password) { setErr('unlock-err', 'Enter your password.'); return; }
  const btn = $('unlock-btn');
  if (btn) btn.disabled = true;
  setErr('unlock-err', '');
  try {
    const stored = await devicevault.recall(email);
    if (!stored) throw new Error('no device vault');
    const opened = await identity.unlockIdentity(password, { salt: stored.salt, vaultBlob: stored.vaultBlob });
    session.set({ email, did: opened.did, handle: opened.handle, jwks: opened.jwks, keys: opened.keys, mnemonic: null });
    if ($('unlock-password')) $('unlock-password').value = '';
    openApp();
  } catch (e) {
    setErr('unlock-err', 'Incorrect password.');
  } finally {
    if (btn) btn.disabled = false;
  }
}

function handleLock() {
  session.lock();
  const st = session.get();
  if ($('gate-password')) $('gate-password').value = '';
  if ($('unlock-password')) $('unlock-password').value = '';
  if (st.email) {
    const g = $('gate-unlock'); if (g) g.dataset.email = st.email;
    const line = $('unlock-email'); if (line) line.textContent = st.email;
    showGate('unlock');
    refreshUnlockBio(st.email);
  } else {
    setAuthMode('register');
    showGate('auth');
  }
  toast('Locked.');
}

// ---------- recovery ("Forgot password?") ----------
let rec = {};
function openRecover() {
  rec = {};
  setErr('recover-err', '');
  const known = session.get().email || (($('gate-unlock') || {}).dataset || {}).email || '';
  if ($('recover-email')) $('recover-email').value = known;
  showGate('recover');
}

async function handleRecoverSend(ev) {
  if (ev) ev.preventDefault();
  const email = (($('recover-email') || {}).value || '').trim();
  if (!email) { setErr('recover-err', 'Enter your email.'); return; }
  const btn = $('recover-send'); const lbl = btn && btn.textContent;
  if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
  setErr('recover-err', '');
  try {
    const [{ nonce }, svc] = await Promise.all([api.recoveryChallenge(email), serviceRecoveryRequest(email)]);
    rec = { email, nonce, challengeId: svc && svc.challengeId };
    setText('recover-email-label', email);
    setShow($('recover-second'), false);
    if ($('recover-code')) $('recover-code').value = '';
    setErr('recover-code-err', '');
    showGate('recover-code');
  } catch (e) {
    setErr('recover-err', friendly(e));
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = lbl; }
  }
}

async function handleRecoverVerify() {
  const code = (($('recover-code') || {}).value || '').trim();
  if (!code) { setErr('recover-code-err', 'Enter the code from your email.'); return; }
  if (!rec.challengeId) { setErr('recover-code-err', 'No recovery code was found for that email.'); return; }
  const btn = $('recover-verify'); if (btn) btn.disabled = true;
  setErr('recover-code-err', '');
  try {
    const S = await serviceRecoveryVerify(rec.challengeId, code);
    if (!S) throw new Error('invalid code');
    rec.serviceShare = S;
    setShow($('recover-second'), true);
  } catch (e) {
    setErr('recover-code-err', 'That code didn’t work. Check your email and try again.');
  } finally {
    if (btn) btn.disabled = false;
  }
}

function useUserSlice() {
  const code = (($('recover-userslice') || {}).value || '').trim();
  if (!code) { setErr('recover-code-err', 'Paste your saved recovery code, or choose “use my secure backup”.'); return; }
  rec.secondShare = code;
  if ($('recover-newpass')) $('recover-newpass').value = '';
  setErr('recover-newpass-err', '');
  showGate('recover-newpass');
}

async function requestLostSlice() {
  try {
    const r = await api.recoveryCompanySlice(rec.email, rec.nonce);
    if (r && r.shareCompany) { rec.secondShare = r.shareCompany; showGate('recover-newpass'); return; }
    if (r && r.availableAt) {
      const when = new Date(r.availableAt);
      setText('recover-pending-msg', (r.message || 'For your protection, releasing your secure backup is delayed.') + ' Available after ' + when.toLocaleString() + '.');
    }
    showGate('recover-pending');
  } catch (e) { setErr('recover-code-err', friendly(e)); }
}

async function checkLostSlice() {
  const btn = $('recover-check'); if (btn) btn.disabled = true;
  try {
    const r = await api.recoveryCompanySlice(rec.email, rec.nonce);
    if (r && r.shareCompany) { rec.secondShare = r.shareCompany; if ($('recover-newpass')) $('recover-newpass').value = ''; showGate('recover-newpass'); return; }
    toast('Not ready yet — your secure backup will be available soon.');
  } catch (e) { toast(friendly(e)); }
  finally { if (btn) btn.disabled = false; }
}

async function handleRecoverFinish(ev) {
  if (ev) ev.preventDefault();
  const newPassword = ($('recover-newpass') || {}).value || '';
  if (!newPassword) { setErr('recover-newpass-err', 'Choose a new password.'); return; }
  if (!rec.serviceShare || !rec.secondShare) { setErr('recover-newpass-err', 'Missing a recovery slice — please start over.'); return; }
  const btn = $('recover-finish'); const lbl = btn && btn.textContent;
  if (btn) { btn.disabled = true; btn.textContent = 'Recovering…'; }
  setErr('recover-newpass-err', '');
  try {
    const opened = await recoverFlow({ email: rec.email, newPassword, serviceShare: rec.serviceShare, secondShare: rec.secondShare, nonce: rec.nonce });
    await devicevault.remember(rec.email, opened).catch(() => {});
    session.set({ email: rec.email, did: opened.did, handle: opened.handle, jwks: opened.jwks, keys: opened.keys, mnemonic: null });
    rec = {};
    openApp();
    toast('Welcome back — your account is recovered.');
  } catch (e) {
    setErr('recover-newpass-err', friendly(e));
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = lbl; }
  }
}

// ---------- biometric unlock (WebAuthn PRF) ----------
async function refreshBioRow() {
  const el = $('bioToggle'); if (!el) return;
  if (!(await webauthn.available())) { el.textContent = 'Unavailable'; el.style.opacity = '.55'; el.style.pointerEvents = 'none'; return; }
  const email = session.get().email;
  const enrolled = email ? await devicevault.recallBiometric(email).catch(() => null) : null;
  el.textContent = enrolled ? 'On' : 'Turn on';
  el.classList.toggle('done', !!enrolled);
}
async function refreshUnlockBio(email) {
  const btn = $('unlock-bio'); if (!btn) return;
  const enrolled = email ? await devicevault.recallBiometric(email).catch(() => null) : null;
  setShow(btn, !!enrolled);
}
function openBioModal() { setErr('bio-err', ''); if ($('bio-password')) $('bio-password').value = ''; setShow($('bio-modal'), true); }
function closeBioModal() { setShow($('bio-modal'), false); }
async function confirmEnableBio() {
  const email = session.get().email;
  const password = ($('bio-password') || {}).value || '';
  if (!password) { setErr('bio-err', 'Enter your password.'); return; }
  const btn = $('bio-confirm'); if (btn) btn.disabled = true;
  setErr('bio-err', '');
  try {
    const stored = await devicevault.recall(email);
    if (!stored) throw new Error('no device vault');
    await identity.unlockIdentity(password, { salt: stored.salt, vaultBlob: stored.vaultBlob }); // verify the password
    const enrollment = await webauthn.enroll(email, password);
    await devicevault.rememberBiometric(email, enrollment);
    closeBioModal();
    toast('Face ID is on for this device.');
    refreshBioRow();
  } catch (e) {
    const m = (e && e.message) || '';
    setErr('bio-err', /decrypt|cipher|operation-specific/i.test(m) ? 'Incorrect password.' : friendly(e));
  } finally { if (btn) btn.disabled = false; }
}
async function handleBioUnlock() {
  const email = session.get().email || (($('gate-unlock') || {}).dataset || {}).email;
  if (!email) return;
  const btn = $('unlock-bio'); if (btn) btn.disabled = true;
  setErr('unlock-err', '');
  try {
    const enrollment = await devicevault.recallBiometric(email);
    if (!enrollment) throw new Error('no enrollment');
    const password = await webauthn.unlock(email, enrollment);
    const stored = await devicevault.recall(email);
    const opened = await identity.unlockIdentity(password, { salt: stored.salt, vaultBlob: stored.vaultBlob });
    session.set({ email, did: opened.did, handle: opened.handle, jwks: opened.jwks, keys: opened.keys, mnemonic: null });
    openApp();
  } catch (e) {
    setErr('unlock-err', 'Face ID didn’t work — use your password.');
  } finally { if (btn) btn.disabled = false; }
}

// ---------- issuance inbox ----------
const escHtml = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const humanizeType = (t) => String(t).replace(/Credential$/, '').replace(/([a-z])([A-Z])/g, '$1 $2').trim() || 'Credential';
function credLabel(item) {
  try { const d = vcjwt.decodeVc(item.payload.vc); const types = (d.payload.vc && d.payload.vc.type) || []; return humanizeType(types[types.length - 1] || 'Credential'); }
  catch (_) { return 'Credential'; }
}
const CHECK_SVG = '<svg viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4 4 10-10.5" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
// A value-carrying issuance (a redeemable credit/royalty the platform is sending you) — read the
// value/currency/app from the signed VC so the inbox can announce "$X arriving" before the on-chain
// token is indexed.
function issuanceInfo(item) {
  try {
    const d = vcjwt.decodeVc(item.payload.vc);
    const cs = (d.payload.vc && d.payload.vc.credentialSubject) || {};
    if (cs.value == null) return null;
    return { value: cs.value, currency: cs.currency || 'USD', app: cs.app || null };
  } catch (_) { return null; }
}
function vcHasType(item, t) {
  try { const d = vcjwt.decodeVc(item.payload.vc); const types = (d.payload.vc && d.payload.vc.type) || []; return types.includes(t); }
  catch (_) { return false; }
}
function fmtWhen(ms) { if (!ms) return ''; try { return new Date(ms).toLocaleDateString(); } catch (_) { return ''; } }

// A signed receipt the paying app delivers after a payout — rendered as money history on the Wallet.
function refreshRedemptions(items) {
  const rlist = $('redemptions-list'); if (!rlist) return;
  const receipts = items.filter((i) => vcHasType(i, 'RedemptionReceipt')).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  rlist.innerHTML = receipts.map((i) => {
    const iv = issuanceInfo(i) || {};
    const from = iv.app ? ordinals.appInfo(iv.app).label : (i.issuerName || 'app');
    const amt = iv.value != null ? ordinals.fmtValue(iv.value, iv.currency) + ' redeemed' : 'Redeemed';
    return '<div class="frow"><div class="av" style="background:var(--green-soft);color:var(--green)">✓</div>'
      + '<div class="t"><b>' + escHtml(amt) + '</b><span>' + escHtml(from) + ' · ' + escHtml(fmtWhen(i.createdAt)) + '</span></div></div>';
  }).join('');
  setShow($('redemptions-section'), receipts.length > 0);
}

async function refreshInbox() {
  if (!$('proveChips')) return;
  let items = [];
  try { items = (await api.inbox()).items || []; } catch (_) { items = []; }
  refreshRedemptions(items); // receipts render as Wallet money-history, not credentials
  // Receipts are informational — keep them out of the credential inbox + chips.
  const pending = items.filter((i) => i.status === 'pending' && !vcHasType(i, 'RedemptionReceipt'));
  const claimed = items.filter((i) => i.status === 'claimed' && !vcHasType(i, 'RedemptionReceipt'));
  const card = $('inbox-card');
  if (card) card.innerHTML = pending.map((i) => {
    const iv = issuanceInfo(i);
    if (iv) { // money-flavored: "$12.50 from Rare Generation — arriving in your wallet"
      const from = iv.app ? ordinals.appInfo(iv.app).label : (i.issuerName || i.issuerDid);
      return '<div class="opt"><div class="ic" style="background:var(--green-soft);color:var(--green)">💵</div>'
        + '<div class="t"><b>' + escHtml(ordinals.fmtValue(iv.value, iv.currency)) + ' redeemable</b><span>from ' + escHtml(from) + ' · arriving in your wallet</span></div>'
        + '<span class="pill-state done" data-claim="' + i.itemId + '" style="cursor:pointer">Add</span></div>';
    }
    return '<div class="opt"><div class="ic" style="background:var(--accent-soft);color:var(--accent)">📩</div>'
      + '<div class="t"><b>' + escHtml(credLabel(i)) + '</b><span>from ' + escHtml(i.issuerName || i.issuerDid) + '</span></div>'
      + '<span class="pill-state done" data-claim="' + i.itemId + '" style="cursor:pointer">Add</span></div>';
  }).join('');
  setShow($('inbox-section'), pending.length > 0);
  const chips = $('proveChips');
  chips.innerHTML = claimed.length
    ? claimed.map((i) => '<span class="proof">' + CHECK_SVG + ' ' + escHtml(credLabel(i)) + '</span>').join('')
    : '<span class="sub" style="margin:0">No verified credentials yet — they’ll appear here when an app issues one to you.</span>';
}

// ---------- collection (real 1Sat Ordinals holdings, read-only) ----------
const escAttr = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
async function refreshCollection() {
  const st = session.get();
  const addr = st.keys && st.keys.tokensOrdinals && st.keys.tokensOrdinals.address;
  const box = $('collection-groups');
  if (!addr || !box) return;
  setText('ord-address', addr);
  let items = [];
  try { items = await ordinals.fetchCollection(addr); } catch (_) { items = []; }
  window.__tokens = items;
  refreshRedeemable();
  if (!items.length) { box.innerHTML = '<div class="tok-empty">No tokens yet. Assets you buy or receive from SmartLedger apps — collectibles, music, tickets, ownership tokens — show up here.</div>'; return; }
  const groups = ordinals.groupByApp(items);
  box.innerHTML = groups.map((g) =>
    '<div class="tok-group"><div class="tok-head"><div class="ic">' + escHtml(g.icon) + '</div><div class="lb">' + escHtml(g.label) + '</div><div class="ct">' + g.items.length + '</div></div>'
    + '<div class="tok-grid">' + g.items.map((it) => tokenTile(it, items.indexOf(it))).join('') + '</div></div>').join('');
}

// Wallet home leads with the redeemable value — the point of the product (BSV is secondary).
// Sums held, non-spent tokens by currency and counts the apps they came from.
async function refreshRedeemable() {
  const card = $('redeemable-card'); if (!card) return;
  const st = session.get();
  const addr = st.keys && st.keys.tokensOrdinals && st.keys.tokensOrdinals.address;
  if (!addr) { card.style.display = 'none'; return; }
  let items = window.__tokens;
  if (!items) { try { items = await ordinals.fetchCollection(addr); window.__tokens = items; } catch (_) { items = []; } }
  const redeemable = (items || []).filter((it) => it.value != null && !ordinals.isSpent(it.status));
  if (!redeemable.length) { card.style.display = 'none'; return; }
  const byCur = {}; const apps = new Set();
  redeemable.forEach((it) => { const c = it.currency || 'USD'; byCur[c] = (byCur[c] || 0) + (Number(it.value) || 0); if (it.app) apps.add(it.app); });
  const curs = Object.keys(byCur).sort((a, b) => byCur[b] - byCur[a]);
  setText('rs-amount', ordinals.fmtValue(byCur[curs[0]], curs[0]) + (curs.length > 1 ? ' +' : ''));
  const n = apps.size;
  setText('rs-apps', n ? (n + ' app' + (n > 1 ? 's' : '')) : (redeemable.length + ' token' + (redeemable.length > 1 ? 's' : '')));
  card.style.display = '';
}

function tokenTile(it, idx) {
  const kind = ordinals.mediaKind(it.contentType);
  let media;
  if (kind === 'image') media = '<img loading="lazy" src="' + escAttr(it.contentUrl) + '" alt="">';
  else if (kind === 'video') media = '<video muted playsinline preload="metadata" src="' + escAttr(it.contentUrl) + '"></video><span class="kind">▶ video</span>';
  else if (kind === 'audio') media = '<div class="glyph">🎵</div><span class="kind">audio</span>';
  else media = '<div class="glyph">◆</div>';
  const val = ordinals.fmtValue(it.value, it.currency);
  const spent = ordinals.isSpent(it.status);
  const overlay = (val && !spent ? '<span class="tok-val">' + escHtml(val) + '</span>' : '') + (spent ? '<div class="used">USED</div>' : '');
  const sub = it.collection ? escHtml(it.collection) : (it.app ? escHtml(it.app) : '1Sat Ordinal');
  return '<div class="tok" data-tok="' + idx + '"><div class="m">' + media + overlay + '</div>'
    + '<div class="cap"><b>' + escHtml(it.name) + '</b><span>' + sub + '</span></div></div>';
}

function openToken(it) {
  const kind = ordinals.mediaKind(it.contentType);
  const m = $('token-media');
  if (m) {
    if (kind === 'image') m.innerHTML = '<img src="' + escAttr(it.contentUrl) + '" alt="">';
    else if (kind === 'video') m.innerHTML = '<video src="' + escAttr(it.contentUrl) + '" controls playsinline></video>';
    else if (kind === 'audio') m.innerHTML = '<div class="glyph">🎵</div><audio src="' + escAttr(it.contentUrl) + '" controls></audio>';
    else m.innerHTML = '<div class="glyph">◆</div>';
  }
  setText('token-title', it.name);
  const appEl = $('token-app');
  if (appEl) {
    if (it.app) { const info = ordinals.appInfo(it.app); appEl.style.display = ''; appEl.textContent = info.icon + ' ' + info.label + (it.collection ? ' · ' + it.collection : ''); }
    else appEl.style.display = 'none';
  }
  // Redeemable value + action — the point of most tokens here (platform value, not BSV).
  const spent = ordinals.isSpent(it.status);
  const val = ordinals.fmtValue(it.value, it.currency);
  const valEl = $('token-value');
  if (valEl) {
    if (val) { valEl.style.display = ''; valEl.innerHTML = escHtml(val) + '<small>' + (spent ? 'already ' + escHtml(it.status || 'redeemed') : 'redeemable') + '</small>'; }
    else valEl.style.display = 'none';
  }
  window.__openToken = it;
  const redeem = $('token-redeem');
  if (redeem) {
    if (it.redeemUrl && !spent) { redeem.style.display = ''; redeem.href = '#'; redeem.textContent = ordinals.actionLabel(it.kind) + (it.app ? ' on ' + ordinals.appInfo(it.app).label : ''); }
    else redeem.style.display = 'none';
  }
  const rows = [];
  if (it.collection) rows.push(['Collection', it.collection]);
  if (it.kind) rows.push(['Kind', it.kind]);
  if (it.status) rows.push(['Status', it.status]);
  rows.push(['Type', it.contentType]);
  Object.keys(it.attrs || {}).forEach((k) => rows.push([k, it.attrs[k]]));
  const attrs = $('token-attrs');
  if (attrs) attrs.innerHTML = rows.map(([k, v]) => '<div class="attr"><span class="k">' + escHtml(k) + '</span><span class="v">' + escHtml(v) + '</span></div>').join('');
  const link = $('token-link'); if (link) link.href = 'https://whatsonchain.com/tx/' + encodeURIComponent(String(it.origin).split('_')[0]);
  setShow($('token-sheet'), true);
}
function closeToken() { const m = $('token-media'); if (m) m.innerHTML = ''; setShow($('token-sheet'), false); }

// Redeem a token: sign a redemption proof with the identity key and hand off to the issuing app,
// which verifies it (POST /api/verify-redeem) then pays out (Stripe/…) + spends the token.
function redeemToken(it) {
  const st = session.get();
  if (!it || !it.redeemUrl || !st.keys || !st.keys.identity || !window.bsv) { toast('Cannot redeem right now.'); return; }
  const identityAddr = st.keys.identity.address;
  const tokenAddr = (st.keys.tokensOrdinals && st.keys.tokensOrdinals.address) || identityAddr;
  const rnd = new Uint8Array(12); (window.crypto || {}).getRandomValues && window.crypto.getRandomValues(rnd);
  const nonce = [...rnd].map((b) => b.toString(16).padStart(2, '0')).join('') || String(Date.now());
  const msg = 'Web3Keys redeem v1\nApp: ' + (it.app || '') + '\nToken: ' + it.origin + '\nAddress: ' + tokenAddr + '\nNonce: ' + nonce;
  let sig;
  try { sig = window.bsv.Message.sign(msg, st.keys.identity.privateKey); } catch (_) { toast('Could not sign the redemption.'); return; }
  let url;
  try {
    const u = new URL(it.redeemUrl);
    u.searchParams.set('token', it.origin); u.searchParams.set('address', tokenAddr);
    u.searchParams.set('identity', identityAddr); u.searchParams.set('nonce', nonce); u.searchParams.set('sig', sig);
    if (it.app) u.searchParams.set('app', it.app);
    url = u.toString();
  } catch (_) { toast('That redeem link is invalid.'); return; }
  toast('Opening ' + (it.app ? ordinals.appInfo(it.app).label : 'the app') + ' to complete your payout…');
  window.open(url, '_blank', 'noopener');
}

// ---------- Wallet: real Finance (BSV) balance, receive address, recent activity ----------
async function refreshWallet() {
  refreshRedeemable(); // lead the wallet with redeemable value (independent of the BSV balance)
  const st = session.get();
  const addr = st.keys && st.keys.finance && st.keys.finance.address;
  if (!addr || !$('bsv-amount')) return;
  // Receive card: paymail (handle@web3keys.com) + address + QR (rendered lazily).
  setText('fin-address', addr);
  const qrEl = $('fin-qr');
  if (qrEl && qrEl.dataset.addr !== addr) { qrEl.innerHTML = qr.qrSvg(addr); qrEl.dataset.addr = addr; } // real, scannable QR of the address
  if (st.handle) {
    const paymail = (window.__username || st.handle) + '@' + (window.location.host || 'web3keys.com');
    setText('fin-paymail', paymail);
    // Register this address so web3keys.com can host paymail for it (idempotent; once per open).
    if (!window.__receiveInfoSet) {
      window.__receiveInfoSet = true;
      const pub = st.keys.finance.pubkey || (st.keys.finance.privateKey && st.keys.finance.privateKey.toPublicKey().toString());
      api.setReceiveInfo(addr, pub).catch(() => { window.__receiveInfoSet = false; });
    }
  }

  const bal = await finance.summary(addr);
  setText('bsv-amount', finance.satsToBsv(bal.total));
  const meta = bal.unavailable
    ? 'Balance temporarily unavailable · keys held on your device'
    : (bal.unconfirmed ? finance.satsToBsv(bal.unconfirmed) + ' BSV pending · ' : '') + 'keys held on your device';
  setText('bal-meta', meta);

  const feed = $('fin-activity'); if (!feed) return;
  const txs = await finance.history(addr);
  if (!txs.length) { feed.innerHTML = '<div class="fin-empty">No activity yet. Your Bitcoin transactions will appear here.</div>'; return; }
  feed.innerHTML = txs.map((t) => {
    const when = t.height ? 'block ' + t.height : 'pending';
    return '<a class="frow" href="https://whatsonchain.com/tx/' + escAttr(t.txid) + '" target="_blank" rel="noopener" style="text-decoration:none;color:inherit">'
      + '<div class="av" style="background:var(--accent-soft)">⛓</div>'
      + '<div class="t"><b>' + escHtml(String(t.txid).slice(0, 18)) + '…</b><span>' + when + '</span></div></a>';
  }).join('');
}

// ---------- Wallet: Send (build + sign in-browser with the Finance key, then broadcast) ----------
let pendingSend = null; // { txHex, fee, amountSats, toAddress }

function resetSend() {
  pendingSend = null;
  const b = $('send-go'); if (b) { b.textContent = 'Review & send'; b.disabled = false; }
  const rv = $('send-review'); if (rv) { rv.style.display = 'none'; rv.innerHTML = ''; }
}

async function sendReview() {
  setText('send-err', '');
  const st = session.get();
  if (!st.keys || !st.keys.finance || !window.bsv) { toast('Unlock your vault first.'); return; }
  const to = (($('send-to') || {}).value || '').trim();
  const amountSats = finance.bsvToSats(($('send-amount') || {}).value);
  if (!to) { setText('send-err', 'Enter a recipient.'); return; }
  if (amountSats < finance.DUST) { setText('send-err', 'Enter an amount of at least ' + finance.satsToBsv(finance.DUST) + ' BSV.'); return; }
  const btn = $('send-go'); if (btn) { btn.disabled = true; btn.textContent = 'Preparing…'; }
  try {
    const rcpt = await finance.resolveRecipient(to, amountSats);
    const addr = st.keys.finance.address;
    const utxos = await finance.utxos(addr);
    const built = finance.buildSend({ bsv: window.bsv, financeKey: st.keys.finance.privateKey, changeAddress: addr, utxos, toAddress: rcpt.address, toScriptHex: rcpt.scriptHex, amountSats });
    pendingSend = built;
    const dest = rcpt.paymail ? escHtml(rcpt.paymail) : escHtml(rcpt.address || 'recipient');
    const rv = $('send-review');
    if (rv) { rv.innerHTML = 'Send <b>' + finance.satsToBsv(amountSats) + ' BSV</b><br>to ' + dest + '<br>network fee ≈ ' + built.fee + ' sats'; rv.style.display = 'block'; }
    if (btn) { btn.disabled = false; btn.textContent = 'Confirm & send'; }
  } catch (e) {
    resetSend();
    setText('send-err', /insufficient|balance/i.test(e && e.message || '') ? 'Not enough balance for this amount + fee.' : ((e && e.message) || 'Could not prepare the payment.'));
  }
}

async function sendConfirm() {
  if (!pendingSend) return sendReview();
  const btn = $('send-go'); if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
  try {
    const { txid } = await api.walletBroadcast(pendingSend.txHex);
    toast('Sent · ' + String(txid).slice(0, 12) + '…');
    ['send-to', 'send-amount'].forEach((id) => { const e = $(id); if (e) e.value = ''; });
    resetSend();
    const sc = $('send-card'); if (sc) sc.style.display = 'none';
    refreshWallet();
  } catch (e) {
    if (btn) { btn.disabled = false; btn.textContent = 'Confirm & send'; }
    setText('send-err', (e && e.message) || 'Broadcast failed.');
  }
}

// ---------- Documents (the flagship: sign any file/text, sealed + verifiable) ----------
const docState = { bytes: null, fileName: null, mimeType: null };

async function previewDocHash() {
  const el = $('doc-hash'); if (!el) return;
  try {
    if (docState.bytes) { el.textContent = 'SHA-256 · ' + (await docsign.hashBytes(docState.bytes)); return; }
    const text = ($('doc-text') || {}).value || '';
    el.textContent = text ? 'SHA-256 · ' + (await docsign.hashText(text)) : '';
  } catch (_) { el.textContent = ''; }
}

function loadDocFile(file) {
  if (!file) return;
  const reader = new FileReader();
  reader.onload = () => {
    docState.bytes = new Uint8Array(reader.result);
    docState.fileName = file.name;
    docState.mimeType = file.type || 'application/octet-stream';
    const fe = $('doc-file'); if (fe) fe.textContent = file.name + ' · ' + Math.max(1, Math.round(file.size / 1024)) + ' KB';
    const te = $('doc-text'); if (te) te.value = '';
    previewDocHash();
  };
  reader.readAsArrayBuffer(file);
}

function clearDocInputs() {
  docState.bytes = null; docState.fileName = null; docState.mimeType = null;
  ['doc-title', 'doc-text'].forEach((id) => { const e = $(id); if (e) e.value = ''; });
  ['doc-file', 'doc-hash', 'doc-err'].forEach((id) => setText(id, ''));
  const fi = $('doc-fileinput'); if (fi) fi.value = '';
}

async function signDoc() {
  const st = session.get();
  if (!st.keys || !st.did) { toast('Unlock your vault first.'); return; }
  setText('doc-err', '');
  let docHash; let fileName = null; let mimeType = 'text/plain';
  if (docState.bytes) {
    docHash = await docsign.hashBytes(docState.bytes); fileName = docState.fileName; mimeType = docState.mimeType;
  } else {
    const text = ($('doc-text') || {}).value || '';
    if (!text.trim()) { setText('doc-err', 'Drop a file or paste some text to sign.'); return; }
    docHash = await docsign.hashText(text);
  }
  const title = (($('doc-title') || {}).value || '').trim() || fileName || 'Untitled document';
  const btn = $('doc-sign'); if (btn) { btn.disabled = true; btn.textContent = 'Sealing…'; }
  try {
    const vc = docsign.signDocument({ did: st.did, privateKey: st.keys.identity.privateKey, docHash, title, fileName, mimeType, version: 1 });
    await api.signDocument(vc);
    clearDocInputs();
    toast('Document signed & sealed.');
    refreshDocuments();
  } catch (e) {
    setText('doc-err', (e && e.message) || 'Could not sign the document.');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Sign document'; }
  }
}

async function refreshDocuments() {
  const list = $('doc-list'); if (!list) return;
  const st = session.get(); if (!st.handle) return;
  let docs = [];
  try { docs = (await api.documents()).docs || []; } catch (_) { docs = []; }
  window.__signedDocs = docs;
  if (!docs.length) { list.innerHTML = '<div class="doc-empty">Nothing signed yet. Sign your first document above.</div>'; return; }
  list.innerHTML = docs.map((d, i) => {
    const when = d.signedAt ? new Date(d.signedAt * 1000).toLocaleDateString() : '';
    const meta = 'Signed ' + when + (d.fileName ? ' · ' + escHtml(d.fileName) : '') + ' · v' + (d.version || 1);
    const anchor = d.anchorTxid
      ? '<a class="doc-anchor on" href="https://whatsonchain.com/tx/' + escAttr(d.anchorTxid) + '" target="_blank" rel="noopener">⛓ Anchored on Bitcoin</a>'
      : '<span class="doc-anchor off">Sealed in registry</span>';
    return '<div class="doc-item"><div class="dt">' + escHtml(d.title || 'Untitled document') + '</div>'
      + '<div class="dm">' + meta + '</div><div class="dh">' + escHtml(d.docHash) + '</div>'
      + '<div class="doc-anchor-row">' + anchor + '</div>'
      + '<div class="da"><button data-copyvc="' + i + '">Copy proof</button><button data-verify="' + i + '">Verify</button></div></div>';
  }).join('');
}

// ---------- Permissions (connected apps + revoke) ----------
async function refreshPermissions() {
  const list = $('perm-list'); if (!list) return;
  const st = session.get(); if (!st.handle) return;
  let grants = [];
  try { grants = (await api.permissions()).grants || []; } catch (_) { grants = []; }
  if (!grants.length) { list.innerHTML = '<div class="doc-empty">No apps connected yet. When you “Sign in with Web3Keys”, they’ll appear here.</div>'; return; }
  list.innerHTML = grants.map((g) => {
    const when = g.grantedAt ? new Date(g.grantedAt).toLocaleDateString() : '';
    return '<div class="doc-item"><div class="dt">' + escHtml(g.clientName || g.clientId) + '</div>'
      + '<div class="dm">' + escHtml(g.scope || 'openid') + ' · connected ' + when + '</div>'
      + '<div class="da"><button class="perm-revoke" data-revoke="' + escAttr(g.clientId) + '">Revoke access</button></div></div>';
  }).join('');
}

// ---------- Selective disclosure (share cards: sign only the facts you choose) ----------
function shareRowHtml(label, value) {
  return '<div class="share-row"><input class="sr-label" placeholder="Fact (e.g. Over 18)" value="' + escAttr(label || '') + '">'
    + '<input class="sr-value" placeholder="Value (e.g. Yes)" value="' + escAttr(value || '') + '">'
    + '<button class="sr-rm" title="Remove" type="button">×</button></div>';
}
function renderShareRows(seed) {
  const box = $('share-rows'); if (!box) return;
  const rows = seed || [['', ''], ['', '']];
  box.innerHTML = rows.map((r) => shareRowHtml(r[0], r[1])).join('');
}
function readShareFields() {
  const fields = {};
  document.querySelectorAll('#share-rows .share-row').forEach((row) => {
    const k = (row.querySelector('.sr-label').value || '').trim();
    const v = (row.querySelector('.sr-value').value || '').trim();
    if (k && v) fields[k] = v;
  });
  return fields;
}
async function createShareCard() {
  const st = session.get();
  if (!st.keys || !st.did) { toast('Unlock your vault first.'); return; }
  setText('share-err', '');
  const fields = readShareFields();
  if (!Object.keys(fields).length) { setText('share-err', 'Add at least one fact (with a value) to share.'); return; }
  const audience = (($('share-audience') || {}).value || '').trim() || null;
  const ttlDays = Number(($('share-expiry') || {}).value || 0) || 0;
  const btn = $('share-create'); if (btn) { btn.disabled = true; btn.textContent = 'Signing…'; }
  try {
    const vc = disclosure.createDisclosure({ did: st.did, privateKey: st.keys.identity.privateKey, fields, audience, ttlDays });
    await api.createShareCard(vc);
    renderShareRows();
    const a = $('share-audience'); if (a) a.value = '';
    toast('Share card created & signed.');
    refreshShareCards();
  } catch (e) {
    setText('share-err', (e && e.message) || 'Could not create the share card.');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Create share card'; }
  }
}
async function refreshShareCards() {
  const list = $('share-list'); if (!list) return;
  const st = session.get(); if (!st.handle) return;
  let cards = [];
  try { cards = (await api.shareCards()).cards || []; } catch (_) { cards = []; }
  window.__shareCards = cards;
  if (!cards.length) { list.innerHTML = ''; return; }
  const now = Math.floor(Date.now() / 1000);
  list.innerHTML = cards.map((c, i) => {
    const expired = c.expiresAt && c.expiresAt < now;
    const status = c.revoked ? '<span class="pill-mini dead">Revoked</span>' : expired ? '<span class="pill-mini dead">Expired</span>' : '<span class="pill-mini live">Active</span>';
    const exp = c.expiresAt ? 'expires ' + new Date(c.expiresAt * 1000).toLocaleDateString() : 'no expiry';
    const meta = (c.audience ? 'For ' + escHtml(c.audience) + ' · ' : '') + exp;
    const actions = c.revoked ? '' : '<div class="da"><button data-copycard="' + i + '">Copy proof</button><button class="perm-revoke" data-revokecard="' + i + '">Revoke</button></div>';
    return '<div class="doc-item"><div class="dt">' + escHtml((c.labels || []).join(', ') || 'Shared facts') + status + '</div>'
      + '<div class="dm">' + meta + '</div>' + actions + '</div>';
  }).join('');
}
async function revokeShareCard(card, btn) {
  const st = session.get();
  if (!st.keys || !window.bsv) { toast('Unlock your vault first.'); return; }
  if (btn) btn.textContent = 'Revoking…';
  try {
    const signature = window.bsv.Message.sign('revoke:' + card.vcHash, st.keys.identity.privateKey);
    await api.revokeShareCard(card.cardId, signature);
    toast('Share card revoked.');
    refreshShareCards();
  } catch (_) { toast('Could not revoke.'); if (btn) btn.textContent = 'Revoke'; }
}

// ---------- BAP: publish the identity on-chain (verifiable independent of web3keys.com) ----------
function renderBapPublished(bapId, txid) {
  const link = 'https://whatsonchain.com/tx/' + escAttr(txid);
  const st = $('bap-status');
  if (st) st.innerHTML = 'Published · <b>' + escHtml(String(bapId).slice(0, 12)) + '…</b> · <a href="' + link + '" target="_blank" rel="noopener" style="color:var(--accent)">view tx ↗</a>';
  const btn = $('bap-publish'); if (btn) { btn.className = 'pill-state done'; btn.textContent = 'On-chain'; btn.style.cursor = 'default'; }
}
async function refreshBap() {
  const card = $('bap-card'); if (!card) return;
  let enabled = false;
  try { enabled = (await api.anchorConfig()).enabled; } catch (_) { enabled = false; }
  if (!enabled) { card.style.display = 'none'; return; } // no wallet configured → nothing to publish through
  card.style.display = 'block';
  try {
    const s = await api.bapStatus();
    if (s && s.bapTxid) renderBapPublished(s.bapId, s.bapTxid);
  } catch (_) { /* leave the default "not published" prompt */ }
}
async function publishBapIdentity() {
  const st = session.get();
  if (!st.keys || !st.keys.identity || !window.bsv) { toast('Unlock your vault first.'); return; }
  const btn = $('bap-publish'); if (btn) { btn.textContent = 'Publishing…'; btn.style.pointerEvents = 'none'; }
  try {
    const built = bap.buildFromRootKey(st.keys.identity.privateKey);
    const r = await api.publishBap(built.fieldsHex, built.identityKey);
    toast('Identity published on Bitcoin.');
    renderBapPublished(r.bapId, r.txid);
  } catch (e) {
    if (btn) { btn.textContent = 'Publish'; btn.style.pointerEvents = ''; }
    toast((e && e.message) || 'Could not publish.');
  }
}

// ---------- Friendly username → username@web3keys.com paymail + public name ----------
function paymailDomain() { return (typeof window !== 'undefined' && window.location.host) || 'web3keys.com'; }
function showUsername(name) {
  window.__username = name || null;
  setShow($('username-have'), !!name);
  setShow($('username-claimbox'), !name);
  if (name) setText('username-paymail', name + '@' + paymailDomain());
  refreshWallet(); // reflect the friendlier paymail on the Receive card
}
async function refreshUsername() {
  if (!$('username-card')) return;
  try { const r = await api.myUsername(); showUsername(r && r.username); }
  catch (_) { /* leave claim box */ }
}
async function checkUsername() {
  const u = (($('username-input') || {}).value || '').trim().toLowerCase();
  const st = $('username-status'); if (!st) return;
  if (!u) { st.textContent = ''; return; }
  try {
    const r = await api.usernameAvailable(u);
    st.style.color = r.available ? '#0B7A57' : '#DC2626';
    st.textContent = r.available ? '✓ ' + u + '@' + paymailDomain() + ' is available' : (r.reason || 'Not available');
    st.dataset.ok = r.available ? '1' : '';
  } catch (_) { st.textContent = ''; }
}
async function claimUsernameFlow() {
  const u = (($('username-input') || {}).value || '').trim().toLowerCase();
  if (!u) return;
  const btn = $('username-go'); if (btn) { btn.textContent = 'Claiming…'; btn.style.pointerEvents = 'none'; }
  try {
    const r = await api.claimUsername(u);
    toast('Username claimed — you’re ' + r.username + '@' + paymailDomain());
    showUsername(r.username);
  } catch (e) {
    setText('username-status', (e && e.message) || 'Could not claim that name.');
    const s = $('username-status'); if (s) s.style.color = '#DC2626';
  } finally { if (btn) { btn.textContent = 'Claim'; btn.style.pointerEvents = ''; } }
}

async function maybeHandleCancelRecovery() {
  try {
    const q = new URLSearchParams(location.search);
    if (q.get('cancelRecovery') && q.get('email') && q.get('t')) {
      await api.recoveryCancel(q.get('email'), q.get('t')).then(() => toast('Recovery request cancelled.')).catch(() => toast('Could not cancel — it may have expired.'));
    }
  } catch (_) { /* ignore */ }
}

async function decideInitialView() {
  try {
    const s = await api.session();
    if (s && s.email) {
      const stored = await devicevault.recall(s.email);
      if (stored) {
        session.set({ email: s.email });
        const g = $('gate-unlock'); if (g) g.dataset.email = s.email;
        const line = $('unlock-email'); if (line) line.textContent = s.email;
        showGate('unlock');
        refreshUnlockBio(s.email);
        return;
      }
    }
  } catch (_) { /* ignore */ }
  setAuthMode('register');
  showGate('auth');
}

// ---------- boot ----------
function boot() {
  if (!$('gate')) return; // not the app.html page
  const form = $('gate-form');
  if (form) form.addEventListener('submit', handleAuth);
  const toggle = $('gate-toggle');
  if (toggle) toggle.addEventListener('click', (e) => { e.preventDefault(); setAuthMode(authMode === 'register' ? 'login' : 'register'); });
  const ufm = $('unlock-form');
  if (ufm) ufm.addEventListener('submit', handleUnlock);
  const ud = $('unlock-different');
  if (ud) ud.addEventListener('click', async (e) => {
    e.preventDefault();
    await api.logout().catch(() => {});
    session.clear();
    setAuthMode('register');
    showGate('auth');
  });
  const lock = $('lockBtn');
  if (lock) lock.addEventListener('click', (e) => { e.preventDefault(); handleLock(); });

  // recovery ("Forgot password?")
  const click = (id, fn) => { const el = $(id); if (el) el.addEventListener('click', (e) => { e.preventDefault(); fn(); }); };
  click('gate-forgot', openRecover);
  click('unlock-forgot', openRecover);
  const rform = $('recover-form'); if (rform) rform.addEventListener('submit', handleRecoverSend);
  click('recover-back', () => { setAuthMode('login'); showGate('auth'); });
  click('recover-verify', handleRecoverVerify);
  click('recover-use-user', useUserSlice);
  click('recover-lost', requestLostSlice);
  click('recover-check', checkLostSlice);
  click('recover-pending-back', () => showGate('recover-code'));
  const rnpf = $('recover-newpass-form'); if (rnpf) rnpf.addEventListener('submit', handleRecoverFinish);
  click('savecode-copy', () => { const v = $('savecode-value'); if (v && navigator.clipboard) navigator.clipboard.writeText(v.value).then(() => toast('Recovery code copied'), () => {}); else toast('Save your recovery code'); });
  click('savecode-continue', () => { if (pendingSession) { session.set(pendingSession); pendingSession = null; } openApp(); toast('Welcome — your keys are live.'); });
  // biometric (Face ID) unlock
  click('bioToggle', openBioModal);
  click('bio-cancel', closeBioModal);
  click('bio-confirm', confirmEnableBio);
  click('unlock-bio', handleBioUnlock);
  // inbox: claim a pending credential
  const inboxCard = $('inbox-card');
  if (inboxCard) inboxCard.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-claim]'); if (!btn) return;
    btn.textContent = 'Adding…';
    try { await api.inboxClaim(btn.dataset.claim); toast('Credential added to your ID.'); refreshInbox(); }
    catch (_) { toast('Could not add it.'); btn.textContent = 'Add'; }
  });

  window.addEventListener('beforeunload', () => { try { session.lock(); } catch (_) { /* ignore */ } });
  click('ord-copy', () => { const a = ($('ord-address') || {}).textContent; if (a && a !== '—' && navigator.clipboard) navigator.clipboard.writeText(a).then(() => toast('Address copied'), () => {}); });
  window.__refreshInbox = refreshInbox; // hooks for live refresh / tests
  window.__refreshCollection = refreshCollection;
  window.__refreshWallet = refreshWallet;
  // Token collection: tap a token → detail sheet; scrim closes it.
  const tokBox = $('collection-groups');
  if (tokBox) tokBox.addEventListener('click', (e) => { const t = e.target.closest('[data-tok]'); if (t) { const it = (window.__tokens || [])[+t.dataset.tok]; if (it) openToken(it); } });
  click('token-close', closeToken);
  click('token-redeem', () => { if (window.__openToken) redeemToken(window.__openToken); });
  // First-run walkthrough controls
  click('ob-skip', finishOnboarding);
  click('ob-back', () => { if (obStep > 0) { obStep--; renderOnboarding(); } });
  click('ob-next', () => { if (obStep < ONBOARDING.length - 1) { obStep++; renderOnboarding(); } else finishOnboarding(); });
  window.__showOnboarding = showOnboarding; // manual QA + the onboarding e2e (bypasses the webdriver skip)
  // Appearance: System / Light / Dark (persisted; System follows the OS). Applied pre-paint in <head>.
  const seg = $('theme-seg');
  if (seg) {
    const cur = (() => { try { return localStorage.getItem('w3k-theme') || 'system'; } catch (_) { return 'system'; } })();
    const mark = (mode) => seg.querySelectorAll('[data-theme-set]').forEach((b) => b.classList.toggle('on', b.dataset.themeSet === mode));
    mark(cur);
    seg.addEventListener('click', (e) => {
      const b = e.target.closest('[data-theme-set]'); if (!b) return;
      const mode = b.dataset.themeSet;
      try { localStorage.setItem('w3k-theme', mode); } catch (_) {}
      if (mode === 'system') document.documentElement.removeAttribute('data-theme');
      else document.documentElement.setAttribute('data-theme', mode);
      mark(mode);
    });
  }

  // Wallet: reveal Receive / Send panels, copy the finance address
  const toggleCard = (el) => { if (el) el.style.display = (el.style.display === 'none' || !el.style.display) ? 'block' : 'none'; };
  click('btn-receive', () => { toggleCard($('receive-card')); const s = $('send-card'); if (s) s.style.display = 'none'; });
  click('btn-send', () => { toggleCard($('send-card')); const r = $('receive-card'); if (r) r.style.display = 'none'; resetSend(); setText('send-err', ''); });
  click('fin-copy', () => { const a = ($('fin-address') || {}).textContent; if (a && a !== '—' && navigator.clipboard) navigator.clipboard.writeText(a).then(() => toast('Address copied'), () => {}); });
  click('paymail-copy', () => { const p = ($('fin-paymail') || {}).textContent; if (p && p !== '—' && navigator.clipboard) navigator.clipboard.writeText(p).then(() => toast('Paymail copied'), () => {}); });
  click('req-copy', () => {
    const st = session.get();
    const alias = window.__username || st.handle;
    if (!alias) { toast('Sign in to create a request link.'); return; }
    const amt = (($('req-amount') || {}).value || '').trim();
    const n = Number(amt);
    if (amt && (!isFinite(n) || n <= 0)) { toast('Enter a valid amount.'); return; }
    const link = window.location.origin + '/@' + alias + (amt ? '?amount=' + encodeURIComponent(amt) : '');
    if (navigator.clipboard) navigator.clipboard.writeText(link).then(() => toast(amt ? 'Request link copied' : 'Pay link copied'), () => {});
  });
  click('send-go', () => (pendingSend ? sendConfirm() : sendReview()));
  // Editing the recipient/amount after a review invalidates the prepared tx.
  ['send-to', 'send-amount'].forEach((id) => { const e = $(id); if (e) e.addEventListener('input', () => { if (pendingSend) resetSend(); }); });
  window.__refreshDocuments = refreshDocuments;
  window.__refreshPermissions = refreshPermissions;

  // Documents: file picker + drag/drop + live hash preview + sign
  const fileInput = $('doc-fileinput');
  const drop = $('doc-drop');
  if (drop && fileInput) {
    drop.addEventListener('click', () => fileInput.click());
    fileInput.addEventListener('change', () => loadDocFile(fileInput.files && fileInput.files[0]));
    ['dragenter', 'dragover'].forEach((ev) => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.add('drag'); }));
    ['dragleave', 'drop'].forEach((ev) => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.remove('drag'); }));
    drop.addEventListener('drop', (e) => { if (e.dataTransfer && e.dataTransfer.files[0]) loadDocFile(e.dataTransfer.files[0]); });
  }
  const docText = $('doc-text'); if (docText) docText.addEventListener('input', () => { docState.bytes = null; setText('doc-file', ''); previewDocHash(); });
  click('doc-sign', signDoc);
  const docList = $('doc-list');
  if (docList) docList.addEventListener('click', (e) => {
    const docs = window.__signedDocs || [];
    const cp = e.target.closest('[data-copyvc]');
    if (cp) { const d = docs[+cp.dataset.copyvc]; if (d && navigator.clipboard) navigator.clipboard.writeText(d.vc).then(() => toast('Proof copied — paste it on the verify page.'), () => {}); return; }
    const vf = e.target.closest('[data-verify]');
    if (vf) { const d = docs[+vf.dataset.verify]; if (d && navigator.clipboard) navigator.clipboard.writeText(d.vc).catch(() => {}); window.open('/verify.html', '_blank'); }
  });
  const permList = $('perm-list');
  if (permList) permList.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-revoke]'); if (!btn) return;
    btn.textContent = 'Removing…';
    try { await api.revokePermission(btn.dataset.revoke); toast('Access removed.'); refreshPermissions(); }
    catch (_) { toast('Could not remove access.'); btn.textContent = 'Revoke access'; }
  });

  // Share cards: add/remove fact rows, create, copy proof, revoke
  window.__refreshShareCards = refreshShareCards;
  window.__refreshBap = refreshBap;
  click('bap-publish', () => { const b = $('bap-publish'); if (b && b.className.indexOf('done') === -1) publishBapIdentity(); });
  // Username: live availability (debounced) + claim
  let unmeT;
  const uin = $('username-input');
  if (uin) uin.addEventListener('input', () => { clearTimeout(unmeT); unmeT = setTimeout(checkUsername, 300); });
  click('username-go', claimUsernameFlow);
  click('share-addrow', () => { const box = $('share-rows'); if (box) box.insertAdjacentHTML('beforeend', shareRowHtml('', '')); });
  const shareRows = $('share-rows');
  if (shareRows) shareRows.addEventListener('click', (e) => { const rm = e.target.closest('.sr-rm'); if (rm && shareRows.querySelectorAll('.share-row').length > 1) rm.closest('.share-row').remove(); });
  click('share-create', createShareCard);
  const shareList = $('share-list');
  if (shareList) shareList.addEventListener('click', (e) => {
    const cards = window.__shareCards || [];
    const cp = e.target.closest('[data-copycard]');
    if (cp) { const c = cards[+cp.dataset.copycard]; if (c && navigator.clipboard) navigator.clipboard.writeText(c.vc).then(() => toast('Proof copied — paste it on the verify page.'), () => {}); return; }
    const rv = e.target.closest('[data-revokecard]');
    if (rv) { const c = cards[+rv.dataset.revokecard]; if (c) revokeShareCard(c, rv); }
  });

  setAuthMode('register');
  maybeHandleCancelRecovery();
  decideInitialView();
}

if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') window.addEventListener('DOMContentLoaded', boot);
  else boot();
}

module.exports = Web3Keys;
